/**
 * Import card: drop a file → see what is in it → choose where it goes.
 *
 *   drop → preview (counts, columns we could not place, new options)
 *        → "New game" (details prefilled from the file name) → Import → done
 *        → "Existing game" (best match preselected) → Review → reconcile step
 *
 * Reconciling needs the full page width, so the page takes over from here
 * via `onReconcile`.
 */
import { useId, useMemo, useRef, useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { DeskSection } from '../../components/desk-section';
import type {
  HudlColumnOverride,
  HudlCommitResult,
  HudlExportField,
  HudlImportPreview,
  HudlReconcileResult,
  HudlValueDecision,
} from '../../lib/api-types';
import { APP_VIEWS } from '../../lib/constants';

import { candidateDetail, candidateLabel, plural, toMessage } from './hudl-format';
import { defaultValueDecisions, NewOptionsPanel } from './new-options-panel';

/** Server scores candidates 0–100 (opponent name 60 + same date 40); ≥ 60 = likely this game. */
const STRONG_MATCH_SCORE = 60;

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.xlsx,.csv';
const KEEP = '';
const IGNORE = '__ignore';

export interface ReconcileHandoff {
  preview: HudlImportPreview;
  result: HudlReconcileResult;
  overrides: HudlColumnOverride[];
  valueDecisions: Map<string, HudlValueDecision>;
}

export interface ImportCardProps {
  fields: readonly HudlExportField[];
  onReconcile(handoff: ReconcileHandoff): void;
  onImported(): void;
}

type Step =
  | { kind: 'pick' }
  | { kind: 'uploading'; fileName: string }
  | { kind: 'preview'; preview: HudlImportPreview }
  | { kind: 'done'; preview: HudlImportPreview; result: HudlCommitResult };

export function gamePath(tenantSlug: string | null, eventId: string): string {
  return `/app/${tenantSlug ?? ''}/${APP_VIEWS.events}/${eventId}`;
}

export function ImportCard({ fields, onReconcile, onImported }: ImportCardProps): JSX.Element {
  const api = useApiClient();
  const { tenantSlug } = useSession();
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>({ kind: 'pick' });
  const [pickError, setPickError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  async function upload(file: File): Promise<void> {
    setPickError(null);
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.csv')) {
      setPickError('Choose an Excel (.xlsx) or CSV (.csv) file.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setPickError('That file is larger than 10 MB. Export a single game from Hudl and try again.');
      return;
    }
    setStep({ kind: 'uploading', fileName: file.name });
    try {
      const preview = await api.previewHudlImport(file);
      setStep({ kind: 'preview', preview });
    } catch (caught) {
      setStep({ kind: 'pick' });
      setPickError(toMessage(caught, 'We could not read that file. Please try again.'));
    }
  }

  function onDrop(dropEvent: DragEvent<HTMLDivElement>): void {
    dropEvent.preventDefault();
    setIsDragging(false);
    const file = dropEvent.dataTransfer.files[0];
    if (file) void upload(file);
  }

  const reset = (): void => {
    setStep({ kind: 'pick' });
    setPickError(null);
  };

  return (
    <DeskSection
      title="Import from Hudl"
      description="Bring in a Hudl playlist as a new game, or line it up with a game you already tracked."
      className="hudl-card"
      data-testid="hudl-import"
    >
      {step.kind === 'pick' || step.kind === 'uploading' ? (
        <>
          <div
            className={`hudl-drop${isDragging ? ' is-dragging' : ''}${step.kind === 'uploading' ? ' is-busy' : ''}`}
            onDragOver={(dragEvent) => {
              dragEvent.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            data-testid="hudl-drop"
          >
            {step.kind === 'uploading' ? (
              <p className="hudl-drop__title" role="status">
                <span className="hudl-spinner" aria-hidden="true" /> Reading {step.fileName}…
              </p>
            ) : (
              <>
                <span className="hudl-drop__icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 16V4" />
                    <path d="m7 9 5-5 5 5" />
                    <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
                  </svg>
                </span>
                <div className="hudl-drop__text">
                  <p className="hudl-drop__title">Drop a Hudl spreadsheet here</p>
                  <p className="muted hudl-drop__hint">Excel (.xlsx) or CSV, up to 10 MB</p>
                </div>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => inputRef.current?.click()}
                >
                  Choose file
                </button>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="visually-hidden"
              tabIndex={-1}
              aria-label="Hudl spreadsheet"
              data-testid="hudl-file-input"
              onChange={(changeEvent) => {
                const file = changeEvent.target.files?.[0];
                changeEvent.target.value = '';
                if (file) void upload(file);
              }}
            />
          </div>
          {pickError === null ? null : (
            <p className="field__error" role="alert" data-testid="hudl-pick-error">
              {pickError}
            </p>
          )}
          <p className="muted hudl-card__foot">
            In Hudl, open the playlist, choose <em>Export → Data (Excel)</em>, and drop the file
            here.
          </p>
        </>
      ) : null}

      {step.kind === 'preview' ? (
        <PreviewStep
          key={step.preview.upload_id}
          preview={step.preview}
          fields={fields}
          onCancel={reset}
          onReconcile={onReconcile}
          onCommitted={(result) => {
            setStep({ kind: 'done', preview: step.preview, result });
            onImported();
          }}
        />
      ) : null}

      {step.kind === 'done' ? (
        <div className="hudl-done" data-testid="hudl-import-done">
          <div className="hudl-done__icon" aria-hidden="true">
            ✓
          </div>
          <div>
            <p className="hudl-done__title" role="status">
              Imported {plural(step.result.created + step.result.inserted, 'play')} into{' '}
              {step.result.event_name}
            </p>
            {step.result.options_created > 0 ? (
              <p className="muted">
                {plural(step.result.options_created, 'new option')} added to your playbook.
              </p>
            ) : null}
          </div>
          <div className="hudl-done__actions">
            <Link className="btn btn--primary" to={gamePath(tenantSlug, step.result.event_id)}>
              Open the game
            </Link>
            <button type="button" className="btn btn--ghost" onClick={reset}>
              Import another file
            </button>
          </div>
        </div>
      ) : null}
    </DeskSection>
  );
}

interface PreviewStepProps {
  preview: HudlImportPreview;
  fields: readonly HudlExportField[];
  onCancel(): void;
  onReconcile(handoff: ReconcileHandoff): void;
  onCommitted(result: HudlCommitResult): void;
}

function PreviewStep({
  preview,
  fields,
  onCancel,
  onReconcile,
  onCommitted,
}: PreviewStepProps): JSX.Element {
  const api = useApiClient();
  const ids = useId();
  const best = preview.candidate_events[0] ?? null;
  const [destination, setDestination] = useState<'new' | 'existing'>(
    best !== null && best.score >= STRONG_MATCH_SCORE ? 'existing' : 'new'
  );
  const [name, setName] = useState(preview.suggested.name);
  const [opponent, setOpponent] = useState(preview.suggested.opponent_name ?? '');
  const [date, setDate] = useState(preview.suggested.date ?? '');
  const [eventId, setEventId] = useState(best?.event_id ?? '');
  const [columnChoice, setColumnChoice] = useState<Record<string, string>>({});
  const [valueDecisions, setValueDecisions] = useState(() =>
    defaultValueDecisions(preview.unknown_values)
  );
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unplaced = preview.columns.filter((column) => column.status !== 'mapped');
  const overrides = useMemo<HudlColumnOverride[]>(
    () =>
      Object.entries(columnChoice)
        .filter(([, value]) => value !== KEEP)
        .map(([header, value]) => ({ header, field: value === IGNORE ? null : value })),
    [columnChoice]
  );
  const target = preview.candidate_events.find((event) => event.event_id === eventId) ?? null;
  const isClosed = destination === 'existing' && target?.status === 'closed';
  const { counts } = preview;

  async function importNew(): Promise<void> {
    if (name.trim() === '') {
      setError('Give the game a name.');
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      const result = await api.commitHudlImport(preview.upload_id, {
        target: {
          kind: 'new',
          name: name.trim(),
          opponent_name: opponent.trim() === '' ? null : opponent.trim(),
          date: date === '' ? null : date,
          event_type: 'game',
        },
        value_actions: [...valueDecisions.values()],
        column_overrides: overrides.length === 0 ? undefined : overrides,
      });
      onCommitted(result);
    } catch (caught) {
      setError(
        toMessage(caught, 'The import did not finish. Nothing was saved. Please try again.')
      );
    } finally {
      setIsBusy(false);
    }
  }

  async function review(): Promise<void> {
    if (eventId === '') return;
    setIsBusy(true);
    setError(null);
    try {
      const result = await api.reconcileHudlImport(preview.upload_id, {
        event_id: eventId,
        column_overrides: overrides.length === 0 ? undefined : overrides,
      });
      onReconcile({ preview, result, overrides, valueDecisions });
    } catch (caught) {
      setError(
        toMessage(caught, 'We could not line the file up with that game. Please try again.')
      );
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <div className="hudl-preview" data-testid="hudl-preview">
      <div className="hudl-file">
        <span className="hudl-file__icon" aria-hidden="true">
          XLS
        </span>
        <div className="hudl-file__text">
          <strong className="hudl-file__name" title={preview.file_name}>
            {preview.file_name}
          </strong>
          <span className="muted">
            {preview.format === 'hudl' ? 'Hudl playlist spreadsheet' : 'Spreadsheet'}
          </span>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
          Choose another file
        </button>
      </div>

      <dl className="hudl-stats">
        <div className="hudl-stat hudl-stat--total">
          <dt>Plays</dt>
          <dd>{counts.plays}</dd>
        </div>
        <div className="hudl-stat">
          <dt>Offense</dt>
          <dd>{counts.offense}</dd>
        </div>
        <div className="hudl-stat">
          <dt>Defense</dt>
          <dd>{counts.defense}</dd>
        </div>
        <div className="hudl-stat">
          <dt>Special teams</dt>
          <dd>{counts.special_teams}</dd>
        </div>
      </dl>
      {counts.skipped_clips > 0 ? (
        <p className="muted hudl-preview__note">
          {plural(counts.skipped_clips, 'clip')} without a play (timeouts, sideline) will be left
          out.
        </p>
      ) : null}

      {unplaced.length === 0 ? null : (
        <details
          className="hudl-unplaced"
          open={unplaced.some((column) => column.status === 'ignored')}
        >
          <summary>{plural(unplaced.length, 'column')} we don&rsquo;t track directly</summary>
          <p className="muted">
            By default these are kept as team stats (or left out if empty). You can match any of
            them to a Gridiron field instead.
          </p>
          <ul className="hudl-unplaced__list">
            {unplaced.map((column) => (
              <li key={column.header} className="hudl-unplaced__row">
                <div className="hudl-unplaced__col">
                  <strong>{column.header}</strong>
                  <span className="muted hudl-unplaced__sample">
                    {column.sample.length === 0 ? 'empty' : column.sample.slice(0, 3).join(', ')}
                  </span>
                </div>
                <select
                  className="field__control"
                  value={columnChoice[column.header] ?? KEEP}
                  onChange={(changeEvent) =>
                    setColumnChoice((current) => ({
                      ...current,
                      [column.header]: changeEvent.target.value,
                    }))
                  }
                  aria-label={`Match column ${column.header} to`}
                >
                  <option value={KEEP}>
                    {column.status === 'custom' ? 'Keep as team stat' : 'Leave out'}
                  </option>
                  {column.status === 'custom' ? <option value={IGNORE}>Leave out</option> : null}
                  {fields.map((field) => (
                    <option key={field.field} value={field.field}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </details>
      )}

      <fieldset className="hudl-dest">
        <legend className="hudl-dest__legend">Where should these plays go?</legend>
        <div className="hudl-dest__options">
          <label className={`hudl-dest__option${destination === 'new' ? ' is-active' : ''}`}>
            <input
              type="radio"
              name={`${ids}-dest`}
              checked={destination === 'new'}
              onChange={() => setDestination('new')}
            />
            <span>
              <strong>New game</strong>
              <span className="muted">Create a game from this file</span>
            </span>
          </label>
          <label
            className={`hudl-dest__option${destination === 'existing' ? ' is-active' : ''}${preview.candidate_events.length === 0 ? ' is-disabled' : ''}`}
          >
            <input
              type="radio"
              name={`${ids}-dest`}
              checked={destination === 'existing'}
              disabled={preview.candidate_events.length === 0}
              onChange={() => setDestination('existing')}
            />
            <span>
              <strong>Existing game</strong>
              <span className="muted">
                {preview.candidate_events.length === 0
                  ? 'You have no games to add to yet'
                  : 'Fill gaps and review differences'}
              </span>
            </span>
          </label>
        </div>

        {destination === 'new' ? (
          <div className="hudl-dest__form" data-testid="hudl-new-game">
            <label className="field hudl-dest__wide">
              <span className="field__label">Game name</span>
              <input
                className="field__control"
                value={name}
                maxLength={120}
                onChange={(changeEvent) => setName(changeEvent.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">Opponent</span>
              <input
                className="field__control"
                value={opponent}
                maxLength={120}
                onChange={(changeEvent) => setOpponent(changeEvent.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">Date</span>
              <input
                type="date"
                className="field__control"
                value={date}
                onChange={(changeEvent) => setDate(changeEvent.target.value)}
              />
            </label>
          </div>
        ) : (
          <div className="hudl-dest__form" data-testid="hudl-existing-game">
            <label className="field hudl-dest__wide">
              <span className="field__label">Game</span>
              <select
                className="field__control"
                value={eventId}
                onChange={(changeEvent) => setEventId(changeEvent.target.value)}
              >
                {preview.candidate_events.map((event) => (
                  <option key={event.event_id} value={event.event_id}>
                    {candidateLabel(event)}
                  </option>
                ))}
              </select>
              {target === null ? null : (
                <span className="field__hint">
                  {candidateDetail(target, target === best && best.score >= STRONG_MATCH_SCORE)}
                </span>
              )}
            </label>
            {isClosed ? (
              <p className="banner banner--warning hudl-dest__wide" role="status">
                This game is closed, so it can&rsquo;t take new plays. Reopen it from the game page
                first, or import the file as a new game.
              </p>
            ) : null}
          </div>
        )}
      </fieldset>

      {destination === 'new' ? (
        <NewOptionsPanel
          values={preview.unknown_values}
          decisions={valueDecisions}
          onChange={setValueDecisions}
        />
      ) : null}

      {error === null ? null : (
        <p className="field__error" role="alert" data-testid="hudl-import-error">
          {error}
        </p>
      )}

      <div className="hudl-card__actions">
        {destination === 'new' ? (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void importNew()}
            disabled={isBusy}
            aria-busy={isBusy}
            data-testid="hudl-import-new"
          >
            {isBusy ? <span className="hudl-spinner" aria-hidden="true" /> : null}
            {isBusy ? 'Importing…' : `Import ${plural(counts.plays, 'play')}`}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void review()}
            disabled={isBusy || eventId === '' || isClosed}
            aria-busy={isBusy}
            data-testid="hudl-review"
          >
            {isBusy ? <span className="hudl-spinner" aria-hidden="true" /> : null}
            {isBusy ? 'Comparing…' : 'Compare with this game'}
          </button>
        )}
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={isBusy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
